import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useCurrentTier } from '@/hooks/useCurrentTier';
import {
  useCurrentOrganizationBilling,
  useSetupOrganizationPaymentMutation,
} from '@/hooks/useSwr';

import { useBillingPlanStore } from '.';
import { WelcomeMessagePlanType } from '../../types/billing.type';

export const useHandleSubscribeModalOpen = () => {
  const [, setSearchParams] = useSearchParams();

  const setIsSubscribeModalOpenState = useBillingPlanStore(
    (s) => s.setIsSubscribeModalOpen
  );

  const setShowManageSpend = useBillingPlanStore((s) => s.setShowManageSpend);
  const setShowAddCard = useBillingPlanStore((s) => s.setShowAddCard);
  const setShowClaimGift = useBillingPlanStore((s) => s.setShowClaimGift);
  return useCallback(
    (value: boolean) => {
      if (value === false) {
        setShowAddCard(false);
        setShowManageSpend(false);
        setShowClaimGift(false);
        setSearchParams((prev) => {
          prev.delete('billing_step');
          return prev;
        });
      }
      setIsSubscribeModalOpenState(value);
    },
    [
      setSearchParams,
      setShowAddCard,
      setShowManageSpend,
      setShowClaimGift,
      setIsSubscribeModalOpenState,
    ]
  );
};

export const useSetUpBillingHandlers = () => {
  const { data: currentOrganization } = useCurrentOrganizationBilling();
  const { tier: currentTier, isLegacy } = useCurrentTier();
  const handleSubscribeModalOpen = useHandleSubscribeModalOpen();

  const setClientSecret = useBillingPlanStore((s) => s.setStripeClientSecret);
  const setTargetPlanTier = useBillingPlanStore((s) => s.setTargetPlanTier);
  const setShowManageSpend = useBillingPlanStore((s) => s.setShowManageSpend);
  const setShowAddCard = useBillingPlanStore((s) => s.setShowAddCard);
  const setShowClaimGift = useBillingPlanStore((s) => s.setShowClaimGift);

  const { trigger: updatePayment, isMutating } =
    useSetupOrganizationPaymentMutation((data) => {
      setClientSecret(data.client_secret);
      handleSubscribeModalOpen(true);
    });

  const handleLegacySetupBilling = useCallback(() => {
    updatePayment({});
    if (currentTier === 'developer_legacy') {
      setTargetPlanTier('developer');
    } else {
      setTargetPlanTier('plus');
      setShowManageSpend(true);
    }
    setShowAddCard(true);
    setShowClaimGift(true);
  }, [
    updatePayment,
    setTargetPlanTier,
    setShowManageSpend,
    setShowAddCard,
    setShowClaimGift,
    currentTier,
  ]);

  const handleSubscribeToPlus = () => {
    updatePayment({});
    setTargetPlanTier('plus');
    setShowManageSpend(true);
    if (!currentOrganization?.payment_method) {
      setShowAddCard(true);
    }
  };

  const handleStartupAndPremierEnrollment = (tier: WelcomeMessagePlanType) => {
    updatePayment({});
    setTargetPlanTier(tier);
    setShowAddCard(true);
    setShowManageSpend(true);

    if (isLegacy) {
      setShowClaimGift(true);
    }
  };

  const handleFreeSetupBilling = () => {
    updatePayment({});
    setTargetPlanTier('developer');
    setShowAddCard(true);
  };

  const handleUpdatePaymentMethod = () => {
    updatePayment({});
    setShowAddCard(true);
    setTargetPlanTier(undefined);
  };

  return {
    handleLegacySetupBilling,
    handleSubscribeToPlus,
    handleStartupAndPremierEnrollment,
    handleFreeSetupBilling,
    handleUpdatePaymentMethod,
    isMutating,
  };
};
